import type { ApplicationStatus, BtlApplicationType, BtlPurpose, Channel } from 'api/btlApplicationForm/types';
import type { BorrowerType } from 'api/btlApplications/types';
import type { DocType } from 'enums';
import type { ProductCategoryEnum } from 'legacy/shared-types';

export type ProductCategory = ProductCategoryEnum;

export interface CaseSummary {
  id: string;
  referenceNumber: string;
  owner: string;
  applicantNames: string;
  borrowerType?: BorrowerType;
  status: ApplicationStatus;
  purpose: BtlPurpose;
  submittedAt: Date | null;
  financingAmount: number;
  isTopSlicing: boolean;
  productCategory?: ProductCategory;
  firmName?: string;
  channel?: Channel;
  updatedAt: Date | null;
  createdAt: Date | null;
}

export interface CaseSearchParams {
  q?: string;
  applicationType?: BtlApplicationType;
  page: number;
  limit: number;
}

export interface CaseSearchPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface CaseSearchResponse {
  data: CaseSummary[];
  pagination: CaseSearchPagination;
}

export interface BrokerCasesTasksResponse {
  sections: EvidenceSection[];
}

export enum DocumentReviewStatus {
  ONGOING = 'ONGOING',
  COMPLETED = 'COMPLETED',
}

export enum DocumentReviewType {
  CUSTOMER_FINANCES = 'CUSTOMER_FINANCES',
}

export interface DocumentReviewIssues {
  [EvidenceTaskNameMap.LAST_3_MONTHS_BANK_STATEMENTS]: string[];
  [EvidenceTaskNameMap.LATEST_3_MONTHS_PAYSLIPS]: string[];
}

export interface DocumentReview {
  id: string;
  status: DocumentReviewStatus;
  type: DocumentReviewType;
  createdAt: Date;
  issues: DocumentReviewIssues;
}

export interface BrokerCasesUploadDocPayload {
  individualId?: string;
  type: DocType;
  description?: string;
}

export interface BrokerCasesPatchIndividualPayload {
  shareCode: string;
  taskId: string;
}

export enum EvidenceTaskStatus {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
}

export enum EvidenceTaskType {
  UPLOAD_FILE = 'UPLOAD_FILE',
  SET_SHARE_CODE = 'SET_SHARE_CODE',
}

export const EvidenceSectionNameMap = {
  PROOF_OF_IDENTITY: 'proof-of-identity',
  EMPLOYMENT_INCOME: 'employment-income',
  BANK_STATEMENTS: 'bank-statements',
  PROOF_OF_DEPOSIT: 'proof-of-deposit',
  FURTHER_INFORMATION_REQUIRED: 'further-information-required',
  OTHER_DOCUMENTS: 'other-documents',
} as const;

export type EvidenceSectionName = typeof EvidenceSectionNameMap[keyof typeof EvidenceSectionNameMap];

export const CaseTaskNameMap = {
  BANK_STATEMENT_OR_GIFTED_DEPOSIT: 'case/bank-statement-or-gifted-deposit',
  FURTHER_INFORMATION_REQUIRED: 'case/additional-documents',
  OTHER_DOCUMENTS: 'case/other-documents',
} as const;

export const IndividualTaskNameMap = {
  PROOF_OF_IDENTITY: 'case/individual-proof-of-identity',
  SHARE_CODE_CONFIRMING_VISA_STATUS: 'case/individual-share-code-confirming-visa-status',
  LATEST_3_MONTHS_PAYSLIPS: 'case/individual-latest-3-months-payslips',
  LAST_YEARS_SA302: 'case/individual-last-years-sa302',
  LAST_3_MONTHS_BANK_STATEMENTS: 'case/individual-last-3-months-bank-statements',
  CURRENT_CONTRACT: 'case/individual-current-contract',
} as const;

export const EvidenceTaskNameMap = {
  ...CaseTaskNameMap,
  ...IndividualTaskNameMap,
} as const;

export type EvidenceTaskName = typeof EvidenceTaskNameMap[keyof typeof EvidenceTaskNameMap];

export interface EvidenceTaskBase {
  id: string;
  name: EvidenceTaskName;
  status: EvidenceTaskStatus;
  createdAt: string;
}

export interface EvidenceFileItem {
  id: number;
  description?: string;
  originalFileName: string;
  uploadedAt: string;
}

export interface EvidencePayloadUploadFiles {
  type?: DocType;
  taskType: EvidenceTaskType.UPLOAD_FILE;
  files: EvidenceFileItem[];
  caseId: string;
  individualId?: string;
  /** Custom copy from the API for open-ended tasks (e.g. `case/other-documents`); overrides static tooltip text when set. */
  description?: string;
}

export interface EvidencePayloadSetShareCode {
  taskType: EvidenceTaskType.SET_SHARE_CODE;
  shareCode: string;
  caseId: string;
  individualId: string;
}

export interface EvidenceTaskUploadFiles extends EvidenceTaskBase {
  payload: EvidencePayloadUploadFiles;
  metadata: {
    docIds?: number[];
  };
}

export interface EvidenceTaskSetShareCode extends EvidenceTaskBase {
  payload: EvidencePayloadSetShareCode;
}

export type EvidenceTask = EvidenceTaskUploadFiles | EvidenceTaskSetShareCode;

export interface EvidenceSection {
  name: EvidenceSectionName;
  tasks: EvidenceTask[];
}

export interface EvidenceSectionsData {
  sections: EvidenceSection[];
}

export interface EvidenceProgress {
  totalCount: number;
  completedCount: number;
}
